import * as React from "react";
import { chain } from "underscore";
import * as moment from "moment";

import { Modal, TextInput } from "carbon-components-react";
import { AccountShape, TransactionShape } from "@eave/shared/types/api";
import Money from "../../../Money";

import { EMDASH, acctToTitleStrOnly } from "./shared";
import BaseTable from "web_modules/shared/BaseTable";
import { debounce } from "lodash";
import { isSortableCellObject } from "web_modules/shared/Table/helpers";

const humanize = require("underscore.string/humanize");
const styles = require("./styles.module.scss");

interface TxnsForAccountProps {
  account: AccountShape;
  onHide: Function;
}

export default class TxnsForAccount extends React.Component<TxnsForAccountProps, any> {
  constructor(props) {
    super(props);
    this.state = {
      searchQuery: "",
      results: props?.account?.transactions,
    };
  }

  handleSearchQueryChange = e => {
    this.setState({ searchQuery: e.target.value });
  };

  searchTransactions = (txns): TransactionShape[] => {
    const { searchQuery } = this.state;

    // If a blank search, return all
    if (searchQuery === "") return this.props.account.transactions;

    return txns.filter(txn => {
      const stripped = txn.memo.toLowerCase().replace(/\*/g, "").replace(/\'/g, "");

      return stripped.toLowerCase().includes(searchQuery.toLowerCase());
    });
  };

  debouncedSearch = debounce(
    () => this.setState({ results: this.searchTransactions(this.props.account.transactions) }),
    400,
  );

  render() {
    const { account, onHide } = this.props;
    const { searchQuery, results } = this.state;

    const sortedTxns = chain(results)
      .sortBy((txn: any) => -1 * txn.id)
      .sortBy((txn: any) => -1 * +moment(txn.date)) // higher priority
      .value();

    return (
      <>
        <Modal
          open
          passiveModal
          onBlur={() => onHide()}
          onRequestClose={() => onHide()}
          className={styles.transactionModal}
        >
          <div className={styles.searchBar}>
            <h4 style={{ marginBottom: "10px" }}>Search transactions</h4>
            <TextInput
              id="search-query-input"
              value={searchQuery}
              labelText=""
              onChange={e => {
                // Simple handler for text input values
                this.handleSearchQueryChange(e);

                // Runs the debounced search that updates results
                this.debouncedSearch();
              }}
            />
          </div>
          <div style={{ marginBottom: "20px" }} />
          <div className={styles.transactionsTable}>
            <BaseTable
              key="transactions-for-account"
              title={`Transactions for ${acctToTitleStrOnly(account)}`}
              headerData={[
                {
                  header: "Date",
                  key: "date",
                },
                {
                  header: "Type",
                  key: "eave_transaction_type",
                },
                {
                  header: "Description",
                  key: "memo",
                },
                {
                  header: "Value",
                  key: "value",
                },
              ]}
              rowData={sortedTxns.map((txn: any) => ({
                ...txn,
                id: `${txn.id}`,
                date: moment(txn.date).format("YYYY-MM-DD"),
                eave_transaction_type: txn.eave_transaction_type
                  ? {
                      display: <td>{humanize(txn.eave_transaction_type)}</td>,
                      sortComparisonValue: txn.eave_transaction_type,
                    }
                  : {
                      display: EMDASH,
                      sortComparisonValue: null,
                    },
                memo: txn.memo ? txn.memo.split(",").join(", ") : EMDASH,
                value: {
                  display: <Money key={txn.id} value={txn.value} />,
                  sortComparisonValue: txn.value,
                },
              }))}
              sortRow={(cellA, cellB, { sortDirection, sortStates }) => {
                const A = isSortableCellObject(cellA) ? cellA.sortComparisonValue : cellA;
                const B = isSortableCellObject(cellB) ? cellB.sortComparisonValue : cellB;

                if (sortDirection === sortStates.ASC) {
                  if (!A) return 1;
                  return A.localeCompare(B);
                }

                if (sortDirection === sortStates.DESC) {
                  if (!A) return -1;
                  return B.localeCompare(A);
                }

                return 0;
              }}
              rowHeight="compact"
              useZebraStyles
              isSortable
              disableAddNew
              disableBatchDelete
              disableBatch
              disableBatchEdit
              disableAllControls
            />
          </div>
        </Modal>
      </>
    );
  }
}
